'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Zap,
  ArrowRight,
  Shield,
  Search,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  BookOpen,
  Code,
  BarChart3,
  Globe,
  TrendingDown,
  Target,
  Award,
  FileCode,
  ShoppingCart,
  Brain,
  Link2,
  Cpu,
  Wrench,
  DollarSign,
  Gift,
  Sparkles,
  GraduationCap,
  FlaskConical,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'
import { SiteFooter } from '@/components/ui/site-footer'

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' },
  }),
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const modulos = [
  {
    num: '0',
    titulo: 'Degustacao: O Profissional do Futuro',
    aulas: 4,
    gratis: true,
    icon: GraduationCap,
    descricao:
      'Dados concretos sobre a queda do CTR orgânico com AI Overviews. Diagnóstico profissional gratuito para medir seu nível atual.',
  },
  {
    num: '1',
    titulo: 'Fundamentos Técnicos de SEO Moderno',
    aulas: 5,
    gratis: false,
    icon: Code,
    descricao:
      'Como motores de busca e LLMs indexam, processam e ranqueiam conteúdo. BERT, Core Web Vitals, mobile-first e acessibilidade.',
  },
  {
    num: '2',
    titulo: 'Schema.org e Dados Estruturados',
    aulas: 5,
    gratis: false,
    icon: FileCode,
    descricao:
      'Implementação prática de JSON-LD: Product, Article, FAQ, HowTo, Course. Código pronto para copiar e validar.',
  },
  {
    num: '3',
    titulo: 'Google Merchant Center e Shopping',
    aulas: 4,
    gratis: false,
    icon: ShoppingCart,
    descricao:
      'Configuração completa do Merchant Center: feed de produtos, campanhas Shopping, integração com SEO e IA.',
  },
  {
    num: '4',
    titulo: 'GEO: Otimização para IA Generativa',
    aulas: 5,
    gratis: false,
    icon: Brain,
    descricao:
      'O módulo central. Baseado no paper do KDD 2024 (Princeton, Georgia Tech, IIT Delhi). Citabilidade, fluência, autoridade estatística.',
  },
  {
    num: '5',
    titulo: 'Link Building e Autoridade Digital',
    aulas: 4,
    gratis: false,
    icon: Link2,
    descricao:
      'Estratégias éticas de link building, digital PR, análise de backlink profile e construção de autoridade temática.',
  },
  {
    num: '6',
    titulo: 'SEO Técnico Avançado',
    aulas: 4,
    gratis: false,
    icon: Cpu,
    descricao:
      'JavaScript SEO para SPAs e SSR, internacionalização com hreflang, migração de sites, log analysis e edge SEO.',
  },
  {
    num: '7',
    titulo: 'Ferramentas e Automação',
    aulas: 4,
    gratis: false,
    icon: Wrench,
    descricao:
      'Stack profissional completa: Screaming Frog, Ahrefs/Semrush, dashboards de SEO/GEO e prompts profissionais de IA.',
  },
  {
    num: '8',
    titulo: 'Vendendo Serviços de SEO/GEO',
    aulas: 4,
    gratis: false,
    icon: DollarSign,
    descricao:
      'Precificação de serviços, propostas comerciais, workflow de clientes, como escalar operação e construir portfólio.',
  },
  {
    num: '9',
    titulo: 'Bônus: Cases Reais e Futuro',
    aulas: 3,
    gratis: false,
    icon: Gift,
    descricao:
      'Cases reais de GEO no Brasil, e-commerce + SEO/GEO e tendências 2025-2026: multimodal search, voice search, evolução dos LLMs.',
  },
]

const faq = [
  {
    pergunta: 'Preciso ter experiência com SEO para acompanhar?',
    resposta:
      'Sim, esse é um curso de nível profissional. O ideal é que você já tenha noções básicas de SEO (o que é indexação, o que é uma keyword, como funciona o Google Search Console). Se você está começando do zero, o Pré-SEO/GEO é a porta de entrada correta.',
  },
  {
    pergunta: 'O que é GEO e por que preciso aprender?',
    resposta:
      'GEO (Generative Engine Optimization) é a disciplina de otimizar conteúdo para ser citado por IAs generativas como ChatGPT, Gemini e Perplexity. Segundo o paper do KDD 2024, até 40% das buscas já passam por IA antes de chegar ao Google. Profissionais que não dominarem GEO vão perder relevância nos próximos anos.',
  },
  {
    pergunta: 'O curso inclui código e implementação prática?',
    resposta:
      'Sim. Você vai implementar Schema.org com JSON-LD, configurar Google Merchant Center, analisar logs de servidor, rodar auditorias automatizadas e criar dashboards profissionais. Todo código é fornecido pronto para copiar.',
  },
  {
    pergunta: 'Qual a diferença entre este curso e o Pré-SEO/GEO?',
    resposta:
      'O Pré-SEO/GEO é estratégico e introdutório, ideal para empreendedores e profissionais de marketing que precisam entender o cenário. O SEO/GEO Pro é técnico e prático, para quem vai executar, implementar e vender serviços de SEO e GEO.',
  },
  {
    pergunta: 'O acesso é vitalício?',
    resposta:
      'Sim. Uma vez inscrito, você tem acesso a todas as aulas, materiais, códigos e atualizações futuras do curso. Sem mensalidade, sem taxa extra.',
  },
  {
    pergunta: 'Tem garantia?',
    resposta:
      'Sim. Garantia incondicional de 7 dias. Se por qualquer motivo você sentir que o curso não é para você, basta pedir reembolso. Devolvemos 100% do valor, sem perguntas.',
  },
  {
    pergunta: 'Posso usar no meu trabalho como freelancer ou agência?',
    resposta:
      'Com certeza. O módulo 8 é dedicado a isso: como precificar serviços de SEO/GEO, montar propostas comerciais, construir portfólio e escalar sua operação com processos e automações.',
  },
]

const referencias = [
  'Aggarwal, P. et al. (KDD 2024) - GEO: Generative Engine Optimization',
  'Devlin, J. et al. (2019) - BERT: Pre-training of Deep Bidirectional Transformers',
  'Lewis, P. et al. (2020) - Retrieval-Augmented Generation for Knowledge-Intensive NLP',
  'Noy, A. et al. (2023) - Knowledge Graphs and Large Language Models',
  'Google (2024) - Core Web Vitals Technical Documentation',
  'Schema.org Consortium - Structured Data Vocabulary Specification',
  'Google Search Central (2024) - JavaScript SEO Best Practices',
]

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SeoGeoProPage() {
  const [faqAberto, setFaqAberto] = useState<number | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  function handleCTAClick(e: React.MouseEvent) {
    e.preventDefault()
    setShowCheckout(true)
    setCheckoutError('')
    setTimeout(() => {
      document
        .getElementById('checkout-section')
        ?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  return (
    <main className="min-h-screen bg-gray-950 text-gray-100">
      {/* ── Header ─────────────────────────────────────────── */}
      <header className="w-full py-4 px-6 border-b border-gray-800/60">
        <div className="mx-auto max-w-5xl flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-cyan-400" />
          <span className="text-lg font-semibold text-white">eBuz</span>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-950/80 via-gray-950 to-gray-950" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px] -translate-y-1/2 translate-x-1/4" />

        <div className="relative mx-auto max-w-4xl px-4 pt-16 pb-20 md:pt-24 md:pb-28 text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 px-4 py-1.5 text-sm font-medium text-cyan-300 mb-6">
              <Zap className="h-3.5 w-3.5" />
              42 aulas - 10 módulos - ~10h30 de conteúdo
            </span>
          </motion.div>

          <motion.h1
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
          >
            SEO/GEO Pro
            <br />
            <span className="bg-gradient-to-r from-cyan-400 to-cyan-300 bg-clip-text text-transparent">
              Otimização para IA Generativa
            </span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            Domine SEO técnico e GEO com base no paper do KDD 2024. De
            implementação de Schema.org a vender serviços de otimização para IA.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
          >
            <button
              onClick={handleCTAClick}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:from-cyan-400 hover:to-cyan-500 transition-all"
            >
              Garantir minha vaga
              <ArrowRight className="h-5 w-5" />
            </button>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">
                R$ 297<span className="text-lg font-normal text-gray-400">,00</span>
              </p>
              <p className="text-sm text-gray-500">
                ou 12x de R$ 29,22
              </p>
            </div>
          </motion.div>

          <motion.div
            className="flex items-center justify-center gap-6 mt-8 text-sm text-gray-500"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={4}
          >
            <span className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-cyan-500" />
              7 dias de garantia
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4 text-cyan-500" />
              Acesso vitalício
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-cyan-500" />
              Acesso imediato
            </span>
          </motion.div>
        </div>
      </section>

      {/* ── O SEO tradicional morreu ───────────────────────── */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-4xl px-4 py-16 md:py-20">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            O SEO tradicional não funciona mais sozinho
          </motion.h2>
          <motion.p
            className="text-gray-400 text-center max-w-2xl mx-auto mb-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            Os números não mentem. A busca mudou e quem não se adaptar vai
            desaparecer dos resultados.
          </motion.p>

          <div className="grid gap-6 sm:grid-cols-3">
            {[
              {
                icon: TrendingDown,
                stat: '-40%',
                label: 'Queda no CTR orgânico',
                desc: 'AI Overviews do Google já respondem diretamente, reduzindo cliques em resultados tradicionais.',
              },
              {
                icon: Search,
                stat: '60%',
                label: 'Buscas zero-click',
                desc: 'Mais da metade das buscas no Google terminam sem nenhum clique em link externo.',
              },
              {
                icon: Globe,
                stat: '25M+',
                label: 'Usuários em IAs de busca',
                desc: 'Perplexity, ChatGPT Search e Gemini já são fontes primárias de informação para milhões.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                className="rounded-2xl bg-gray-800/50 border border-gray-700/50 p-6 text-center"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i + 2}
              >
                <item.icon className="h-8 w-8 text-cyan-400 mx-auto mb-3" />
                <p className="text-3xl font-bold text-cyan-400 mb-1">{item.stat}</p>
                <p className="font-semibold text-white text-sm mb-2">{item.label}</p>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Para quem é ────────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-4 py-16 md:py-20">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-white text-center mb-12"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Para quem é esse curso?
        </motion.h2>

        <div className="grid gap-5 sm:grid-cols-2">
          {[
            {
              titulo: 'Profissionais de SEO',
              desc: 'Que querem dominar GEO antes da concorrência e se tornar referência no mercado.',
              icon: Search,
            },
            {
              titulo: 'Analistas de Marketing Digital',
              desc: 'Que precisam entregar resultados orgânicos em um cenário onde as regras mudaram.',
              icon: BarChart3,
            },
            {
              titulo: 'Freelancers e Consultores',
              desc: 'Que querem cobrar mais oferecendo um serviço que quase ninguém domina ainda.',
              icon: Target,
            },
            {
              titulo: 'Donos de Agências',
              desc: 'Que precisam treinar equipes e adicionar GEO ao portfólio de serviços antes que os clientes peçam.',
              icon: Globe,
            },
          ].map((item, i) => (
            <motion.div
              key={item.titulo}
              className="flex gap-4 rounded-2xl bg-gray-800/40 border border-gray-700/40 p-6"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={i + 1}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-cyan-500/15 flex items-center justify-center">
                <item.icon className="h-6 w-6 text-cyan-400" />
              </div>
              <div>
                <p className="font-semibold text-white mb-1">{item.titulo}</p>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── O que você vai dominar (Módulos) ────────────────── */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-4xl px-4 py-16 md:py-20">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            O que você vai dominar
          </motion.h2>
          <motion.p
            className="text-gray-400 text-center max-w-xl mx-auto mb-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            10 módulos, 42 aulas. Do diagnóstico profissional até vender seus
            serviços de SEO/GEO.
          </motion.p>

          <div className="space-y-4">
            {modulos.map((mod, i) => (
              <motion.div
                key={mod.num}
                className="rounded-2xl bg-gray-800/50 border border-gray-700/40 p-5 md:p-6"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.5}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center">
                    <mod.icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-cyan-500 bg-cyan-500/10 px-2 py-0.5 rounded">
                        M{mod.num}
                      </span>
                      <h3 className="font-semibold text-white">{mod.titulo}</h3>
                      {mod.gratis && (
                        <span className="text-xs font-medium text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded">
                          GRATUITO
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{mod.descricao}</p>
                    <p className="text-xs text-gray-500">{mod.aulas} aulas</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sobre o Instrutor ──────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-4 py-16 md:py-20">
        <motion.div
          className="rounded-2xl bg-gray-800/50 border border-gray-700/40 p-8 md:p-10 flex flex-col md:flex-row items-center gap-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <div className="flex-shrink-0 w-28 h-28 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center text-4xl font-bold text-white">
            DA
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Denis Alves</h2>
            <p className="text-cyan-400 font-medium mb-4">
              Instrutor SEO/GEO Pro
            </p>
            <p className="text-gray-400 leading-relaxed">
              Especialista em SEO técnico e otimização para mecanismos de IA
              generativa. Formação em análise de dados e anos de experiência
              prática implementando estratégias de visibilidade digital para
              empresas de diversos portes. Acompanha de perto a evolução dos
              algoritmos de busca e das plataformas de IA, traduzindo pesquisas
              acadêmicas em processos aplicáveis para profissionais e agências.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── Base Científica ─────────────────────────────────── */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-4xl px-4 py-16 md:py-20">
          <motion.div
            className="flex items-center justify-center gap-3 mb-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            <FlaskConical className="h-7 w-7 text-cyan-400" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Base científica sólida
            </h2>
          </motion.div>
          <motion.p
            className="text-gray-400 text-center max-w-2xl mx-auto mb-10"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            29 referências internacionais. Não é achismo: cada técnica ensinada
            tem respaldo em pesquisas publicadas em conferências como KDD, ACL e
            NeurIPS.
          </motion.p>

          <motion.div
            className="rounded-2xl bg-gray-800/40 border border-gray-700/40 p-6 md:p-8"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
          >
            <ul className="space-y-3">
              {referencias.map((ref, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                  <Award className="h-4 w-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span>{ref}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-gray-500 text-center">
              + 22 referências adicionais ao longo dos módulos
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Diferenciais ───────────────────────────────────── */}
      <section className="mx-auto max-w-4xl px-4 py-16 md:py-20">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-white text-center mb-12"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Por que este curso é diferente
        </motion.h2>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              icon: FileCode,
              titulo: 'Código pronto',
              desc: 'Schemas JSON-LD prontos para copiar. Sem reinventar a roda.',
            },
            {
              icon: FlaskConical,
              titulo: 'Paper KDD 2024',
              desc: 'Baseado na pesquisa que definiu GEO como disciplina (Princeton, Georgia Tech, IIT Delhi).',
            },
            {
              icon: DollarSign,
              titulo: 'Módulo de vendas',
              desc: 'Precificação, propostas, workflow de clientes. Tudo para vender serviços de SEO/GEO.',
            },
            {
              icon: Cpu,
              titulo: 'SEO técnico real',
              desc: 'BERT, Core Web Vitals, JavaScript SEO, log analysis. Nível profissional.',
            },
            {
              icon: ShoppingCart,
              titulo: 'Merchant Center',
              desc: 'Módulo dedicado a Google Shopping e integração com SEO para e-commerce.',
            },
            {
              icon: Brain,
              titulo: 'Mentalidade GEO',
              desc: 'Citabilidade, fluência, autoridade estatística. O framework completo para IA generativa.',
            },
          ].map((item, i) => (
            <motion.div
              key={item.titulo}
              className="rounded-2xl bg-gray-800/40 border border-gray-700/40 p-6"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={i + 1}
            >
              <item.icon className="h-7 w-7 text-cyan-400 mb-3" />
              <p className="font-semibold text-white mb-1">{item.titulo}</p>
              <p className="text-sm text-gray-400">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Pricing + Checkout ─────────────────────────────── */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-lg px-4 py-16 md:py-20 text-center">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white mb-2"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            Invista na sua carreira
          </motion.h2>
          <motion.p
            className="text-gray-400 mb-8"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            Acesso vitalício. Sem mensalidade. Atualizações incluídas.
          </motion.p>

          <motion.div
            className="rounded-2xl bg-gray-800/60 border-2 border-cyan-500/40 p-8 mb-6"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
          >
            <p className="text-sm text-cyan-400 font-medium mb-2">SEO/GEO Pro</p>
            <p className="text-4xl font-bold text-white mb-1">
              R$ 297<span className="text-lg font-normal text-gray-400">,00</span>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              ou 12x de R$ 29,22 no cartão
            </p>

            <ul className="text-left space-y-3 mb-8">
              {[
                '42 aulas em 10 módulos',
                'Acesso vitalício + atualizações',
                'Código Schema.org pronto para usar',
                'Módulo de vendas de serviços',
                'Módulo de degustação gratuito',
                'Garantia incondicional de 7 dias',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <button
              onClick={handleCTAClick}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:from-cyan-400 hover:to-cyan-500 transition-all"
            >
              Começar agora
              <ArrowRight className="h-5 w-5" />
            </button>

            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Shield className="h-3.5 w-3.5" />
                Pagamento seguro
              </span>
              <span>PIX, cartão ou boleto</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Checkout ───────────────────────────────────────── */}
      {showCheckout && (
        <section id="checkout-section" className="mx-auto max-w-lg px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Finalizar Compra</h2>
              <button
                onClick={() => setShowCheckout(false)}
                className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            {checkoutError && (
              <div className="mb-4 p-3 bg-red-950/50 border border-red-800 rounded-lg text-sm text-red-300">
                {checkoutError}
              </div>
            )}

            <TransparentCheckout
              channelSlug="seo-geo-pro"
              buyerName={buyerName}
              buyerEmail={buyerEmail}
              buyerPhone={buyerPhone}
              buyerCpf={buyerCpf}
              onSuccess={(paymentId) => {
                router.push('/obrigado?payment=' + paymentId)
              }}
              onError={(error) => {
                setCheckoutError(error)
              }}
            />
          </motion.div>
        </section>
      )}

      {/* ── FAQ ────────────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-4 py-16 md:py-20">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-white text-center mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Perguntas frequentes
        </motion.h2>

        <div className="space-y-3">
          {faq.map((item, i) => (
            <motion.div
              key={i}
              className="rounded-xl bg-gray-800/40 border border-gray-700/40 overflow-hidden"
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={i * 0.3}
            >
              <button
                onClick={() => setFaqAberto(faqAberto === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left"
              >
                <span className="font-medium text-white pr-4">{item.pergunta}</span>
                {faqAberto === i ? (
                  <ChevronUp className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {faqAberto === i && (
                <div className="px-5 pb-5 text-sm text-gray-400 leading-relaxed">
                  {item.resposta}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Cross-sell Combo ───────────────────────────────── */}
      <section className="bg-gray-900/50 border-y border-gray-800/60">
        <div className="mx-auto max-w-3xl px-4 py-16 md:py-20 text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/30 px-4 py-1.5 text-sm font-medium text-amber-300 mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Economize R$ 97
            </span>
          </motion.div>

          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white mb-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            Quer a trilha completa?
          </motion.h2>
          <motion.p
            className="text-gray-400 max-w-xl mx-auto mb-8"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
          >
            O Combo SEO/GEO une o Pré-SEO/GEO (fundamentos e estratégia) com o
            SEO/GEO Pro (técnico e implementação). São 74 aulas pelo preço de 1
            curso e meio.
          </motion.p>

          <motion.div
            className="rounded-2xl bg-gray-800/60 border border-amber-500/30 p-8 inline-block"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={3}
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <p className="text-sm text-gray-500 line-through">R$ 594,00</p>
              <p className="text-3xl font-bold text-white">
                R$ 497<span className="text-base font-normal text-gray-400">,00</span>
              </p>
            </div>
            <p className="text-sm text-gray-400 mb-6">
              Pré-SEO/GEO + SEO/GEO Pro juntos - 74 aulas, 17 módulos
            </p>
            <a
              href="/combo-seo-geo"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-8 py-3.5 font-semibold text-white shadow-lg shadow-amber-500/20 hover:shadow-amber-500/35 hover:from-amber-400 hover:to-amber-500 transition-all"
            >
              Ver Combo SEO/GEO
              <ArrowRight className="h-5 w-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── CTA Final ──────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-4 py-16 md:py-20 text-center">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-white mb-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          O mercado está mudando. Você vai junto ou vai ficar para trás?
        </motion.h2>
        <motion.p
          className="text-gray-400 max-w-xl mx-auto mb-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          Profissionais que dominam GEO hoje vão liderar o mercado nos próximos 3
          anos. O primeiro passo é este.
        </motion.p>
        <motion.button
          onClick={handleCTAClick}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-600 px-10 py-4 text-lg font-semibold text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:from-cyan-400 hover:to-cyan-500 transition-all"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={2}
        >
          Garantir minha vaga por R$ 297
          <ArrowRight className="h-5 w-5" />
        </motion.button>
      </section>

      <SiteFooter />
    </main>
  )
}
